import { useState, useReducer, useEffect } from 'react';
import { isOnServer } from './isOnServer';

/**
 * useLocalStorageState
 * a useState with persistent storage accross sessions
 *
 * Use like React's useState, except the added `key` argument for a unique key
 * for the in-browser local storage
 */
export function useLocalStorageState(key, initialValue) {
  let [localStorageRead, setLocalStorageRead] = useState(false);
  let [storedValue, setStoredValue] = useState(() => valueOrDeferedValue(initialValue));

  useEffect(() => {
    if (localStorageRead) window.localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, localStorageRead, storedValue]);

  useEffect(() => {
    const json = window.localStorage.getItem(key);
    setStoredValue(json && json !== 'undefined' ? JSON.parse(json) : valueOrDeferedValue(initialValue));
    setLocalStorageRead(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setStoredValue];
}

function valueOrDeferedValue(valueOrFunction) {
  return valueOrFunction instanceof Function ? valueOrFunction() : valueOrFunction;
}

/**
 * useLocalStorageReducer
 * a useReducer with persistent storage accross sessions
 *
 * Use like React's useReducer, except the added `key` argument for a unique key
 * for the in-browser local storage
 */
export function useLocalStorageReducer(key, reducer, initialValue, init) {
  let restoreLocalStorage = initialValue => {
    if (isOnServer) return initialValue;
    const json = window.localStorage.getItem(key);

    const storedData = json && json !== 'undefined' ? JSON.parse(json) : initialValue;

    if (init instanceof Function) {
      return init(storedData);
    }

    return storedData;
  };

  let [storedValue, storedValueReducer] = useReducer(reducer, initialValue, restoreLocalStorage);

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(storedValue));
  }, [storedValue, key]);

  return [storedValue, storedValueReducer];
}
